import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  PlayCircle, 
  PauseCircle,
  Loader2, 
  Volume2,
  VolumeX,
  Sparkles,
  Home,
  BookOpen,
  MessageSquare,
  CheckCircle2,
  RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const VideoHolly = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tutorialData, setTutorialData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchTutorialStatus();
  }, []);

  const fetchTutorialStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await fetch(`${API}/api/holly/tutorial-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTutorialData(data);
      }
    } catch (err) {
      console.error('Error fetching tutorial status:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  const hasAudio = tutorialData?.audio?.available;
  const hasVideo = tutorialData?.video?.available;
  const avatarUrl = tutorialData?.avatar_url || '/holly-avatar.png';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
            <Sparkles className="h-5 w-5" />
            <span className="font-medium">Video Tutorial</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
            Ciao, sono Holly!
          </h1>
          <p className="text-lg text-slate-600">
            La tua assistente virtuale HWsmart
          </p>
        </div>

        {/* Main Content Card */}
        <Card className="shadow-xl border-0 overflow-hidden mb-8">
          <CardContent className="p-0">
            {hasVideo && tutorialData?.video?.url ? (
              // Video Player
              <div className="relative">
                <video
                  className="w-full aspect-video bg-slate-900"
                  controls
                  controlsList="noplaybackrate nodownload"
                  disablePictureInPicture
                  poster={tutorialData.video.thumbnail_url}
                  preload="metadata"
                  ref={(el) => {
                    if (el) {
                      // Force playback rate to 1x and block changes
                      el.playbackRate = 1.0;
                      el.addEventListener('ratechange', () => {
                        if (el.playbackRate !== 1.0) el.playbackRate = 1.0;
                      });
                    }
                  }}
                  data-testid="training-video-player"
                >
                  <source src={tutorialData.video.url} type="video/mp4" />
                  Il tuo browser non supporta il tag video.
                </video>
              </div>
            ) : hasAudio ? (
              // Audio Player with Animated Avatar
              <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 p-8">
                {/* Hidden Audio Element */}
                <audio
                  ref={audioRef}
                  src={tutorialData.audio.url}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleEnded}
                  preload="metadata"
                />
                
                <div className="flex flex-col md:flex-row items-center gap-8">
                  {/* Animated Avatar */}
                  <div className="relative">
                    <div className={`w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden border-4 border-white/30 shadow-2xl ${isPlaying ? 'animate-pulse' : ''}`}>
                      <img 
                        src={avatarUrl} 
                        alt="Holly" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Animated ring when playing */}
                    {isPlaying && (
                      <div className="absolute inset-0 rounded-full border-4 border-white/50 animate-ping" style={{ animationDuration: '2s' }} />
                    )}
                  </div>
                  
                  {/* Audio Controls */}
                  <div className="flex-1 text-white text-center md:text-left">
                    <h2 className="text-2xl md:text-3xl font-bold mb-2">Holly</h2>
                    <p className="text-purple-200 mb-6">Tutorial per Responsabili</p>
                    
                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-white transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-sm text-purple-200 mt-1">
                        <span>{formatTime(audioRef.current?.currentTime || 0)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>
                    
                    {/* Control Buttons */}
                    <div className="flex items-center justify-center md:justify-start gap-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-12 w-12 text-white hover:bg-white/20"
                        onClick={handleRestart}
                      >
                        <RotateCcw className="h-6 w-6" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-16 w-16 text-white hover:bg-white/20 rounded-full bg-white/10"
                        onClick={togglePlay}
                      >
                        {isPlaying ? (
                          <PauseCircle className="h-12 w-12" />
                        ) : (
                          <PlayCircle className="h-12 w-12" />
                        )}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-12 w-12 text-white hover:bg-white/20"
                        onClick={toggleMute}
                      >
                        {isMuted ? (
                          <VolumeX className="h-6 w-6" />
                        ) : (
                          <Volume2 className="h-6 w-6" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // No Content Available
              <div className="aspect-video bg-gradient-to-br from-purple-600 to-indigo-700 flex flex-col items-center justify-center text-white p-8">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/30 shadow-xl mb-6">
                  <img 
                    src={avatarUrl} 
                    alt="Holly" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold mb-2">Video in arrivo!</h3>
                <p className="text-purple-100 text-center max-w-md">
                  Il tutorial di Holly sarà disponibile a breve. 
                  Nel frattempo, esplora le sezioni del portale!
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Navigation Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-green-500"
            onClick={() => navigate('/responsabile')}
            data-testid="nav-dashboard"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 rounded-full p-3">
                  <Home className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Dashboard</h3>
                  <p className="text-sm text-slate-500">Arrivi e partenze</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-purple-500"
            onClick={() => navigate('/responsabile/formazione')}
            data-testid="nav-formazione"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 rounded-full p-3">
                  <BookOpen className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Formazione</h3>
                  <p className="text-sm text-slate-500">Video tutorial</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-l-4 border-l-blue-500"
            onClick={() => navigate('/responsabile/comunicazioni')}
            data-testid="nav-comunicazioni"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 rounded-full p-3">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Comunicazioni</h3>
                  <p className="text-sm text-slate-500">Segnalazioni</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Learning Content */}
        <Card className="bg-white/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Cosa imparerai
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-1 mt-0.5">
                  <CheckCircle2 className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-800">Dashboard principale</span>
                  <p className="text-sm text-slate-500">Come visualizzare arrivi, partenze e notifiche importanti</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-1 mt-0.5">
                  <CheckCircle2 className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-800">Gestione immobili</span>
                  <p className="text-sm text-slate-500">Visualizzare dettagli e caratteristiche degli appartamenti assegnati</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-1 mt-0.5">
                  <CheckCircle2 className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-800">Area formazione</span>
                  <p className="text-sm text-slate-500">Completare i video tutorial e superare i quiz di verifica</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-1 mt-0.5">
                  <CheckCircle2 className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-800">Comunicazioni</span>
                  <p className="text-sm text-slate-500">Inviare segnalazioni e comunicare con l'ufficio Holiday World</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="text-center mt-8">
          <Button 
            variant="outline"
            onClick={() => navigate('/responsabile')}
            className="px-8"
            data-testid="back-to-dashboard"
          >
            Torna alla Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VideoHolly;
